import { MoneyField, SwitchButton } from '@/components';
import { useNetwork, useNotifications } from '@/hooks';
import { daysOfWeek } from '@/utils';
import {
  autoMatchDepositSchema,
  applyAutoMatchDepositFallbacks,
} from '@/utils/autoMatchDepositHelpers';
import { Form, Field } from '@availity/form';
import { SelectField } from '@availity/select';
import moment from 'moment';
import React from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Label,
  Row,
  UncontrolledTooltip,
} from 'reactstrap';
import * as yup from 'yup';
import MatchByGroups from './MatchByGroups';
import { FaQuestionCircle } from 'react-icons/fa';

const DaySelectField = ({
  name,
  label,
}: {
  name: string;
  label: string;
}): JSX.Element => (
  <Col xs={3}>
    <SelectField
      name={name}
      label={label}
      valueKey="value"
      labelKey="label"
      options={daysOfWeek}
    />
  </Col>
);

const NetworksAutoMatchDepositsTab: React.FC = () => {
  const { create: createNotification } = useNotifications();
  const { network, update, updating } = useNetwork({
    createNotification,
  });

  return (
    <div>
      <Form
        initialValues={{
          networkID: network?.networkID,
          autoMatchDeposit: network?.autoMatchDeposit
            ? applyAutoMatchDepositFallbacks(network.autoMatchDeposit)
            : {
                enabled: false,
                agentID: '',
                groups: [],
                defaultGroup: null,
                startDay: 0,
                endDay: 0,
              },
        }}
        validationSchema={yup.object().shape({
          autoMatchDeposit: autoMatchDepositSchema,
        })}
        onSubmit={(values) => {
          update({
            variables: {
              input: {
                networkID: network?.networkID,
                brokerID: network?.brokerID,
                autoMatchDeposit: values?.autoMatchDeposit,
              },
            },
          });
        }}
      >
        {({ setFieldValue, values }) => (
          <>
            <Card className="m-3">
              <CardHeader>
                <CardTitle
                  tag="h5"
                  className="py-2 h4 mb-0 text-uppercase d-flex align-items-center"
                >
                  Auto Match Deposit Transfer Config
                </CardTitle>
              </CardHeader>
              <CardBody noGutters>
                <Row>
                  <Col xs={6} style={{ paddingBottom: '10px' }}>
                    <div id="auto-match-deposit-switch-button">
                      <SwitchButton
                        width={100}
                        checked={!!values.autoMatchDeposit.enabled}
                        onlabel="Enabled"
                        offlabel="Disabled"
                        onstyle="success"
                        offstyle="danger"
                        onChange={() =>
                          setFieldValue(
                            'autoMatchDeposit.enabled',
                            !values.autoMatchDeposit.enabled
                          )
                        }
                      />
                    </div>
                    {network?.autoMatchDepositLastModifiedEnabled && (
                      <div style={{ fontSize: '12px' }}>
                        Last Modified{' '}
                        {moment(
                          network?.autoMatchDepositLastModifiedEnabled
                        ).format('MM/DD/YY hh:mm A')}
                      </div>
                    )}
                  </Col>
                </Row>

                <Row>
                  <Col xs={5}>
                    <Field
                      name="autoMatchDeposit.agentID"
                      label="Agent ID"
                      type="text"
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={3}>
                    <Label className="d-flex">
                      Default Auto Fund Group{' '}
                      <FaQuestionCircle
                        id="autoMatchDepositDefaultGroup"
                        className="ml-1 cursor-pointer"
                      />
                      <UncontrolledTooltip
                        target="autoMatchDepositDefaultGroup"
                        style={{ textAlign: 'left' }}
                        placement="auto-start"
                      >
                        Select the default group that new signup agents will be
                        automatically added to.
                      </UncontrolledTooltip>
                    </Label>

                    <SelectField
                      name="autoMatchDeposit.defaultGroup"
                      label=""
                      valueKey="value"
                      labelKey="label"
                      defaultValue={{ value: null, label: 'None' }}
                      options={[
                        { value: null, label: 'None' },
                        ...(values.autoMatchDeposit.groups?.map((_, index) => ({
                          value: index,
                          label: `Group #${index + 1}`,
                        })) || []),
                      ]}
                    />
                  </Col>
                  <DaySelectField
                    name="autoMatchDeposit.startDay"
                    label="Start Day"
                  />
                  <DaySelectField
                    name="autoMatchDeposit.endDay"
                    label="End Day"
                  />
                </Row>
                <Row />
                <Row
                  noGutters
                  style={{ paddingTop: '20px', paddingBottom: '10px' }}
                >
                  <MatchByGroups />
                </Row>
              </CardBody>
            </Card>
            <div className="float-right mr-3 mb-3">
              <Button color="secondary" type="reset" className="mr-2" size="lg">
                Reset
              </Button>
              <Button
                color="primary"
                size="lg"
                type="submit"
                disabled={updating}
              >
                Save
              </Button>
            </div>
          </>
        )}
      </Form>
    </div>
  );
};

export default NetworksAutoMatchDepositsTab;
